
import React from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/feature/LanguageSwitcher';

interface NavigationProps {
  /** 
   * Determines whether the page has been scrolled past a certain point.
   * When `true` a solid background with blur and shadow is applied.
   */
  scrolled: boolean;
}

/**
 * Navigation component – renders the top navigation bar with smooth‑scroll
 * links and a language switcher.
 *
 * The component is deliberately kept lightweight; all DOM interactions are
 * guarded with safety checks and errors are logged instead of causing a crash.
 */
export default function Navigation({ scrolled }: NavigationProps) {
  const { t } = useTranslation();

  /**
   * Scrolls the page to the element with the supplied id.
   * If the element does not exist, a warning is printed to the console.
   *
   * @param id - The id of the target element (without the leading #)
   */
  const scrollToSection = (id: string) => {
    try {
      const element = document.getElementById(id);
      if (!element) {
        console.warn(`Navigation: element with id "${id}" not found.`);
        return;
      }
      element.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error('Navigation: failed to scroll to section', { id, error });
    }
  };

  // Tailwind arbitrary color with opacity needs to be escaped correctly.
  // The slash inside the class string is part of the Tailwind syntax and is safe
  // inside a normal JS string/template literal.
  const navBackground = scrolled
    ? 'bg-[#0f0f0f]/95 backdrop-blur-lg shadow-lg'
    : 'bg-transparent';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBackground}`}
      aria-label="Main navigation"
    >
      <div className="max-w-[1400px] mx-auto px-8 py-6 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <img
            src="https://public.readdy.ai/ai/img_res/986c59b2-a2b4-4fd2-b575-bd141709e178.png"
            alt="Private Technology Concierge"
            className="h-10 w-auto"
          />
        </div>

        {/* Desktop navigation links */}
        <div className="hidden md:flex items-center gap-12">
          <button
            onClick={() => scrollToSection('service')}
            className="text-[#b8b8b8] hover:text-white transition-colors duration-300 text-sm tracking-wide relative group cursor-pointer"
          >
            {t('nav.service')}
            <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-white transition-all duration-300 group-hover:w-full" />
          </button>

          <button
            onClick={() => scrollToSection('pricing')}
            className="text-[#b8b8b8] hover:text-white transition-colors duration-300 text-sm tracking-wide relative group cursor-pointer"
          >
            {t('nav.accessTiers')}
            <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-white transition-all duration-300 group-hover:w-full" />
          </button>

          <button
            onClick={() => scrollToSection('philosophy')}
            className="text-[#b8b8b8] hover:text-white transition-colors duration-300 text-sm tracking-wide relative group cursor-pointer"
          >
            {t('nav.philosophy')}
            <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-white transition-all duration-300 group-hover:w-full" />
          </button>
        </div>

        {/* Right‑side actions */}
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <button
            onClick={() => scrollToSection('apply')}
            className="px-8 py-3 border border-white/30 rounded-full text-white text-sm tracking-wide hover:bg-white/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            {t('nav.requestAccess')}
          </button>
        </div>
      </div>
    </nav>
  );
}
